import { useEffect, useMemo, useState } from 'react';
import { Box, Button, FormControlLabel, MenuItem, Select, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { ptBR } from '@mui/x-date-pickers/locales';
import { Link } from 'react-router-dom';

import moment from 'moment';

import config 			from '../../../common/Config';
import Loading 			from '../../../components/Loading';
import countryList 		from '../../../common/countries';
import useAuth 			from '../../../hooks/useAuth';
import useInternalFetch from '../../../api/apiInternal';

const ROWS_PER_PAGE   = 50;
const TYPE_LABEL      = { ACAO: 'Ação', STOCK: 'Stock' };
const formatQuarter   = (q) => {
	if (!q) return '—'
	const br = q.match(/^([1-4])T(\d{4})$/)
	if (br) return `${br[1]}T${br[2].slice(2)}`
	const us = q.match(/^Q([1-4])\s(\d{4})$/)
	if (us) return `${us[1]}T${us[2].slice(2)}`
	return q
}
const countryMap      = Object.fromEntries(countryList.map(c => [c.code, c.label]));
const datePickerProps = { localeText: ptBR.components.MuiLocalizationProvider.defaultProps.localeText, format: 'DD/MM/YYYY' };

const FlagImg = ({ code }) => code
	? <img loading="lazy" width="20" src={`https://flagcdn.com/w20/${code.toLowerCase()}.png`} srcSet={`https://flagcdn.com/w40/${code.toLowerCase()}.png 2x`} alt={code} />
	: null

function PortfolioSwitch({ value, onChange, disabled }) {
	return (
		<Tooltip title={disabled ? 'Faça login para usar este filtro' : ''}>
			<FormControlLabel
				control={<Switch checked={value} onChange={e => onChange(e.target.checked)} disabled={disabled} size="small" />}
				label="Meu portfólio"
			/>
		</Tooltip>
	)
}

const defaultApplied = {
	from        : moment().format('YYYY-MM-DD'),
	to          : moment().add(7, 'days').format('YYYY-MM-DD'),
	type        : '',
	ticker      : '',
	portfolioOnly: false,
}

function TabUpcomingEarnings() {
	const { auth }          				= useAuth()
	const internalFetch    					= useInternalFetch()
	const [rows, setRows]  					= useState([])
	const [loading, setLoading] 			= useState(true)
	const [page, setPage]   				= useState(0)
	const [countryFilter, setCountryFilter] = useState('')

	// Campos do formulário (não disparam fetch diretamente)
	const [ticker, setTicker]               = useState('')
	const [type, setType]                   = useState('')
	const [from, setFrom]                   = useState(moment())
	const [to, setTo]                       = useState(moment().add(7, 'days'))
	const [portfolioOnly, setPortfolioOnly] = useState(false)

	// Filtros aplicados (disparam o fetch)
	const [applied, setApplied] 			= useState(defaultApplied)

	useEffect(() => {
		setLoading(true); setRows([]); setPage(0)
		const params = new URLSearchParams()
		if (applied.from)                          params.append('from', applied.from)
		if (applied.to)                            params.append('to', applied.to)
		if (applied.type)                          params.append('type', applied.type)
		if (applied.ticker)                        params.append('ticker', applied.ticker.toUpperCase())
		if (applied.portfolioOnly && auth?.userId) params.append('portfolioOnly', '1')
		internalFetch(config.apiUrl + '/api/earnings-calendar?' + params)
			.then(d => { setRows(Array.isArray(d) ? d : []); setLoading(false) })
			.catch(() => setLoading(false))
	}, [applied])

	const handleSearch = () => setApplied({
		from        : from?.format('YYYY-MM-DD') ?? '',
		to          : to?.format('YYYY-MM-DD') ?? '',
		type,
		ticker,
		portfolioOnly,
	})

	const handleClear = () => {
		setTicker(''); setType(''); setPortfolioOnly(false)
		setFrom(moment()); setTo(moment().add(7, 'days'))
		setApplied(defaultApplied)
	}

	const countries = useMemo(() => [...new Set(rows.map(r => r.country).filter(Boolean))].sort(), [rows])
	const filtered  = useMemo(() => rows.filter(r => !countryFilter || r.country === countryFilter), [rows, countryFilter])
	const paginated = useMemo(() => filtered.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE), [filtered, page])

	return (
		<>
			<Stack direction={{ xs: 'column', sm: 'row' }} mb={3} flexWrap="wrap" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ columnGap: 2, rowGap: 1.5 }}>
				<TextField label="Ticker" size="small" value={ticker} onChange={e => setTicker(e.target.value)} sx={{ minWidth: 120 }} />
				<DatePicker label="De" value={from} onChange={setFrom} {...datePickerProps} slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', sm: 145 } } } }} />
				<DatePicker label="Até" value={to} onChange={setTo} {...datePickerProps} slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', sm: 145 } } } }} />
				<Select size="small" value={type} onChange={e => setType(e.target.value)} displayEmpty sx={{ minWidth: 130 }}>
					<MenuItem value="">Todos os tipos</MenuItem>
					<MenuItem value="ACAO">Ações</MenuItem>
					<MenuItem value="STOCK">Stocks</MenuItem>
				</Select>
				<Select size="small" value={countryFilter} onChange={e => setCountryFilter(e.target.value)} displayEmpty sx={{ minWidth: 160 }}>
					<MenuItem value="">Todos os países</MenuItem>
					{countries.map(c => (
						<MenuItem key={c} value={c}>
							<Stack direction="row" alignItems="center" spacing={1}>
								<FlagImg code={c} /><span>{countryMap[c] || c}</span>
							</Stack>
						</MenuItem>
					))}
				</Select>
				<Box sx={{ alignSelf: { xs: 'center', sm: 'auto' } }}>
					<PortfolioSwitch value={portfolioOnly} onChange={setPortfolioOnly} disabled={!auth?.userId} />
				</Box>
				<Stack direction="row" spacing={1} sx={{ flexShrink: 0, alignSelf: { xs: 'center', sm: 'auto' } }}>
					<Button variant="contained" onClick={handleSearch}>Filtrar</Button>
					<Button variant="outlined" onClick={handleClear}>Limpar</Button>
				</Stack>
			</Stack>

			{loading
			? <Box sx={{ minHeight: 200 }}><Loading /></Box>
			: <>
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell><b>Ticker</b></TableCell>
								<TableCell align="center"><b>Trimestre</b></TableCell>
								<TableCell align="center"><b>Publicação</b></TableCell>
								<TableCell align="center"><b>Tipo</b></TableCell>
								<TableCell align="center"><b>País</b></TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{filtered.length === 0 && <TableRow><TableCell colSpan={5} align="center">Nenhum resultado encontrado.</TableCell></TableRow>}
							{paginated.map((row, i) => (
								<TableRow key={i} hover>
									<TableCell><Link to={`/company/${row.ticker}`} style={{ textDecoration: 'none' }}>{row.ticker}</Link></TableCell>
									<TableCell align="center">{formatQuarter(row.quarter)}</TableCell>
									<TableCell align="center">{moment(row.releaseDate).format('DD/MM/YYYY')}</TableCell>
									<TableCell align="center">{TYPE_LABEL[row.type] || row.type}</TableCell>
									<TableCell align="center"><FlagImg code={row.country} /></TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination component="div" count={filtered.length} page={page} onPageChange={(_, p) => setPage(p)}
					rowsPerPage={ROWS_PER_PAGE} rowsPerPageOptions={[ROWS_PER_PAGE]}
					labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`} />
			</>
		}
		</>
	)
}

export default TabUpcomingEarnings
